// EmpLeaves.js
import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Table,
  Spin,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Space,
  Tag,
  Button,
  Timeline,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import EmployeeProfile from "../EmployeeProfile";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Map each tab key to the statuses it should fetch
const TAB_STATUSES = {
  Pending: ["Pending Approval", "Pending Cancellation", "Pending"],
  Approved: ["Approved", "Cancellation Approved", "Approved"],
  Rejected: ["Rejected", "Cancellation Rejected"],
};

export default function EmpLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authData, backUrl } = useContext(AuthContext);

  const [selectedId, setSelectedId] = useState(null);
  const [showEmp, setShowEmp] = useState(false);

  const [Organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });

  const onBack = () => setShowEmp(false);

  // Active tab: "Pending" | "Approved" | "Rejected"
  const [activeStatusTab, setActiveStatusTab] = useState("Pending");

  // Filters state
  const [filters, setFilters] = useState({
    name: "",
    emp_code: "",
    leave_type: "",
    dateRange: [],
    organisation: "",
    department: "",
    designation: "",
  });

  // ── Approve Modal ──
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLeave, setApproveLeave] = useState(null);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [approving, setApproving] = useState(false);

  // ── Reject Modal ──
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectLeave, setRejectLeave] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  // Bulk Action States
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // ---------- Helper accessors ----------
  const getEmployeeFieldId = (employee, fieldBase) => {
    if (!employee) return null;
    if (employee[fieldBase] !== undefined && employee[fieldBase] !== null)
      return employee[fieldBase];
    const alt = `${fieldBase}_id`;
    if (employee[alt] !== undefined && employee[alt] !== null)
      return employee[alt];
    return null;
  };

  const getEmployeeFieldName = (employee, nameFieldCandidates = []) => {
    if (!employee) return "";
    for (const k of nameFieldCandidates) {
      if (employee[k]) return employee[k];
    }
    return "";
  };

  // ---------- Fetch organisations / departments / designations ----------
  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load organisations");
    }
  };

  const fetchDepartments = async (organisationId) => {
    if (!organisationId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${organisationId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignations = async (departmentId) => {
    if (!departmentId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${departmentId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load designations");
    }
  };

  // ---------- Core fetch: supports multiple statuses ----------
  const fetchLeavesByStatuses = async (page = 1, statuses = []) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("is_employee_view", "false");

      if (statuses.length > 0) {
        params.append("status", statuses.join(","));
      }

      const res = await axios.get(
        `${backUrl}/api/hr/leaves/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );

      const data = res.data?.results ?? [];
      setLeaves(data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: res.data?.count ?? data.length,
      }));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Refresh with current tab ----------
  const refreshData = () => {
    fetchLeavesByStatuses(pagination.current, TAB_STATUSES[activeStatusTab]);
  };

  // ---------- Effects ----------
  useEffect(() => {
    fetchOrganisation();
    fetchLeavesByStatuses(1, TAB_STATUSES["Pending"]);
  }, []);

  useEffect(() => {
    if (filters.organisation) {
      fetchDepartments(filters.organisation);
    } else {
      setDepartments([]);
      setDesignations([]);
      setFilters((prev) => ({ ...prev, department: "", designation: "" }));
    }
  }, [filters.organisation]);

  useEffect(() => {
    if (filters.department) {
      fetchDesignations(filters.department);
    } else {
      setDesignations([]);
      setFilters((prev) => ({ ...prev, designation: "" }));
    }
  }, [filters.department]);

  // ---------- Filtering logic ----------
  useEffect(() => {
    let result = [...leaves];

    if (filters.name) {
      const q = filters.name.toLowerCase();
      result = result.filter((item) =>
        `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`
          .toLowerCase()
          .includes(q)
      );
    }

    if (filters.emp_code) {
      const q = filters.emp_code.toLowerCase();
      result = result.filter((item) =>
        (item?.employee?.emp_code ?? "").toLowerCase().includes(q)
      );
    }

    if (filters.leave_type) {
      result = result.filter(
        (item) =>
          (item.leave_type ?? "").toString().toLowerCase() ===
          filters.leave_type.toString().toLowerCase()
      );
    }

    if (filters.dateRange?.length === 2) {
      const [start, end] = filters.dateRange;
      result = result.filter((item) => {
        const itemDate = dayjs(item.start_date);
        return (
          itemDate.isAfter(dayjs(start).subtract(1, "day")) &&
          itemDate.isBefore(dayjs(end).add(1, "day"))
        );
      });
    }

    if (filters.organisation) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empOrgId = getEmployeeFieldId(emp, "organisation");
        const orgName = getEmployeeFieldName(emp, [
          "organisation_name",
          "organisation",
        ]);
        if (/^\d+$/.test(String(filters.organisation))) {
          return String(empOrgId) === String(filters.organisation);
        }
        return (
          String(orgName).toLowerCase() ===
          String(filters.organisation).toLowerCase()
        );
      });
    }

    if (filters.department) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empDeptId = getEmployeeFieldId(emp, "department");
        const deptName = getEmployeeFieldName(emp, [
          "department_name",
          "department",
        ]);
        if (/^\d+$/.test(String(filters.department))) {
          return String(empDeptId) === String(filters.department);
        }
        return (
          String(deptName).toLowerCase() ===
          String(filters.department).toLowerCase()
        );
      });
    }

    if (filters.designation) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empDesigId = getEmployeeFieldId(emp, "designation");
        const desigName = getEmployeeFieldName(emp, [
          "designation_name",
          "designation",
        ]);
        if (/^\d+$/.test(String(filters.designation))) {
          return String(empDesigId) === String(filters.designation);
        }
        return (
          String(desigName).toLowerCase() ===
          String(filters.designation).toLowerCase()
        );
      });
    }

    setFilteredLeaves(result);
  }, [filters, leaves]);

  // ---------- Tab click handler ----------
  const handleTabClick = (tabKey) => {
    setActiveStatusTab(tabKey);
    setSelectedRowKeys([]);
    fetchLeavesByStatuses(1, TAB_STATUSES[tabKey]);
  };

  // ---------- Approve handler ----------
  const handleApprove = async () => {
    if (!approveLeave) return;
    setApproving(true);
    try {
      await axios.post(
        `${backUrl}/api/hr/leaves/approve/`,
        { leave_ids: [approveLeave.id], approver_reason: approveRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success("Leave approved successfully.");
      setShowApproveModal(false);
      setApproveLeave(null);
      setApproveRemarks("");
      refreshData();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Approval failed";
      toast.error(errorMsg);
    } finally {
      setApproving(false);
    }
  };

  // ---------- Reject handler ----------
  const handleReject = async () => {
    if (!rejectLeave) return;
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }
    setRejecting(true);
    try {
      await axios.post(
        `${backUrl}/api/hr/leaves/reject/`,
        { leave_ids: [rejectLeave.id], rejection_reason: rejectReason },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success("Leave rejected successfully.");
      setShowRejectModal(false);
      setRejectLeave(null);
      setRejectReason("");
      refreshData();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Rejection failed";
      toast.error(errorMsg);
    } finally {
      setRejecting(false);
    }
  };

  // ---------- Bulk Approve ----------
  const handleBulkApprove = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }
    setBulkProcessing(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/hr/leaves/approve/`,
        { leave_ids: selectedRowKeys, approver_reason: bulkRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(
        res?.data?.message || res?.data?.detail || "Leaves approved successfully"
      );
      setBulkRemarks("");
      setShowBulkApprove(false);
      setSelectedRowKeys([]);
      refreshData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk approval failed"
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Bulk Reject ----------
  const handleBulkReject = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }
    if (!bulkRemarks.trim()) {
      toast.warning("Please provide a reason for rejection");
      return;
    }
    setBulkProcessing(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/hr/leaves/reject/`,
        { leave_ids: selectedRowKeys, rejection_reason: bulkRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(
        res?.data?.message || res?.data?.detail || "Leaves rejected successfully"
      );
      setBulkRemarks("");
      setShowBulkReject(false);
      setSelectedRowKeys([]);
      refreshData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk rejection failed"
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Excel Download ----------
  const downloadExcel = () => {
    const rows = filteredLeaves.map((item) => ({
      "Employee Name": `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`.trim(),
      "Employee Code": item?.employee?.emp_code ?? "-",
      Designation:
        getEmployeeFieldName(item.employee, [
          "designation_name",
          "designation",
        ]) ||
        getEmployeeFieldId(item.employee, "designation") ||
        "-",
      "Leave Type": item.leave_type ?? "-",
      Duration: item.duration_type ?? "-",
      Days: item.leave_days_count ?? "-",
      "From Date": item.start_date
        ? new Date(item.start_date).toLocaleDateString("en-IN")
        : "-",
      "To Date": item.end_date
        ? new Date(item.end_date).toLocaleDateString("en-IN")
        : "-",
      Reason: item.reason ?? "-",
      Status: item.status ?? "-",
      Organisation:
        getEmployeeFieldName(item.employee, [
          "organisation_name",
          "organisation",
        ]) ||
        getEmployeeFieldId(item.employee, "organisation") ||
        "-",
      Department:
        getEmployeeFieldName(item.employee, [
          "department_name",
          "department",
        ]) ||
        getEmployeeFieldId(item.employee, "department") ||
        "-",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Leaves");
    XLSX.writeFile(workbook, `${activeStatusTab}_Leaves.xlsx`);
  };

  // ---------- Table pagination change ----------
  const handleTableChange = (pag) => {
    const newPage = pag.current;
    setPagination((prev) => ({ ...prev, current: newPage }));
    fetchLeavesByStatuses(newPage, TAB_STATUSES[activeStatusTab]);
  };

  // ---------- Row Selection ----------
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
    getCheckboxProps: (record) => ({ name: record.id }),
  };

  // ---------- Table columns ----------
  const isHR = authData?.employee?.role !== 2;

  const columns = [
    {
      title: "Employee Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      render: (text, record) => (
        <a
          onClick={() => {
            setSelectedId(record.employee.id);
            setShowEmp(true);
          }}
          style={{ cursor: "pointer", color: "#1890ff" }}
        >
          {text}
        </a>
      ),
      width: 180,
    },
    {
      title: "Employee Name",
      key: "name",
      fixed: "left",
      render: (_, record) =>
        `${record?.employee?.first_name ?? ""} ${record?.employee?.last_name ?? ""}`.trim(),
    },
    isHR && {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            shape="circle"
            icon={<CheckOutlined />}
            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
            onClick={() => {
              setApproveLeave(record);
              setApproveRemarks("");
              setShowApproveModal(true);
            }}
          />
          <Button
            danger
            type="primary"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={() => {
              setRejectLeave(record);
              setRejectReason("");
              setShowRejectModal(true);
            }}
          />
        </Space>
      ),
    },
    {
      title: "Designation",
      key: "designation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "designation_name",
          "designation",
        ]) ||
        getEmployeeFieldId(record.employee, "designation") ||
        "-",
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
      render: (text, record) => (
        <>
          <p>{text}</p>
          {(record.leave_type === "Compoff" || record.leave_type === "Weekoff") &&
            record.work_dates?.length > 0 && (
              <div style={{ marginTop: "4px", fontSize: "12px", color: "#1890ff" }}>
                <strong>Work Dates:</strong> {record.work_dates.join(", ")}
              </div>
            )}
        </>
      ),
    },
    { title: "Duration", dataIndex: "duration_type", key: "duration_type" },
    { title: "Days", dataIndex: "leave_days_count", key: "leave_days_count" },
    {
      title: "From Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "To Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    { title: "Reason", dataIndex: "reason", key: "reason" },
    {
      title: "Approval Hierarchy",
      key: "remarks",
      width: 400,
      render: (_, record) => {
        const approvals = record.approvals || [];
        if (!approvals.length) return "-";
        return (
          <Timeline style={{ marginTop: "20px" }}>
            {approvals.map((a, index) => {
              const statusColor =
                a.status === "Approved" || a.status === "Cancellation Approved"
                  ? "green"
                  : a.status === "Rejected" || a.status === "Cancellation Rejected"
                    ? "red"
                    : "gray";

              return (
                <Timeline.Item key={index} color={statusColor}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <Text strong style={{ fontSize: "14px" }}>
                          {a.approver_name ||
                            (a.approver?.first_name || a.approver?.last_name
                              ? `${a.approver.first_name || ""} ${a.approver.last_name || ""}`
                              : (a.approver?.user?.first_name || a.approver?.user?.last_name
                                ? `${a.approver.user.first_name || ""} ${a.approver.user.last_name || ""}`
                                : (a.approver?.user?.username || "-"))).trim()}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          {a.approver?.designation_name || "Approver"}
                        </Text>
                      </div>
                      <Tag color={statusColor}>{a.status === "Cancelled" ? "Skipped (Leave Cancelled)" : a.status}</Tag>
                    </div>

                    {/* Action Timestamps */}
                    {/* Combined Action Details (Additive) */}
                    <div style={{ marginTop: "6px" }}>
                      {/* Regular Approval / Rejection Block */}
                      {(a.approved_on || a.rejected_on || a.approver_reason || a.rejection_reason) && (
                        <div style={{ marginBottom: "8px" }}>
                          {(a.approved_on || a.rejected_on) && (
                            <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                              <Text strong style={{ fontSize: "11px", color: "#595959" }}>{a.rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                              {dayjs(a.approved_on || a.rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                            </div>
                          )}
                          {(a.approver_reason || a.rejection_reason) && (
                            <div style={{ fontSize: "13px" }}>
                              <Text strong>{a.rejection_reason ? "Rejection Remark: " : "Remark: "}</Text>
                              <Text>{a.rejection_reason || a.approver_reason}</Text>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Cancellation Block */}
                      {(a.cancellation_approved_on || a.cancellation_rejected_on || a.cancellation_reason || a.cancellation_rejected_reason) && (
                        <div style={{
                          marginTop: (a.approved_on || a.rejected_on) ? "8px" : "0",
                          paddingTop: (a.approved_on || a.rejected_on) ? "8px" : "0",
                          borderTop: (a.approved_on || a.rejected_on) ? "1px dashed #e8e8e8" : "none"
                        }}>
                          {(a.cancellation_approved_on || a.cancellation_rejected_on) && (
                            <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                              <Text strong style={{ fontSize: "11px", color: "#595959" }}>Cancellation {a.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                              {dayjs(a.cancellation_approved_on || a.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                            </div>
                          )}
                          {(a.cancellation_reason || a.cancellation_rejected_reason) && (
                            <div style={{ fontSize: "13px" }}>
                              <Text strong>Cancellation Remark: </Text>
                              <Text>{a.cancellation_rejected_reason || a.cancellation_reason}</Text>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "Approved" || status === "Cancellation Approved"
            ? "green"
            : status === "Rejected" || status === "Cancellation Rejected"
              ? "red"
              : status === "Cancelled"
                ? "gray"
                : "#faad14";
        return (
          <span style={{ color, fontWeight: 600 }}>{status ?? "-"}</span>
        );
      },
    },
    {
      title: "Organisation",
      key: "organisation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "organisation_name",
          "organisation",
        ]) ||
        getEmployeeFieldId(record.employee, "organisation") ||
        "-",
    },
    {
      title: "Department",
      key: "department_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "department_name",
          "department",
        ]) ||
        getEmployeeFieldId(record.employee, "department") ||
        "-",
    },
  ].filter(Boolean);

  // ---------- Tab config ----------
  const tabs = [
    {
      key: "Pending",
      label: "Pending",
      activeColor: "#faad14",
    },
    {
      key: "Approved",
      label: "Approved",
      activeColor: "#4CAF50",
    },
    {
      key: "Rejected",
      label: "Rejected",
      activeColor: "#f5222d",
    },
  ];

  // ---------- Render ----------
  return (
    <div style={{ padding: "20px" }}>
      {showEmp ? (
        <EmployeeProfile
          empId={selectedId}
          onBack={onBack}
          DisableEditOption={true}
        />
      ) : (
        <>
          <ToastContainer />
          <Title
            level={4}
            style={{
              marginBottom: 16,
              textTransform: "uppercase",
              color: "#0e497b",
            }}
          >
            Employee Leave Records
          </Title>

          {/* Status Tab Buttons */}
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            {tabs.map((tab) => {
              const isActive = activeStatusTab === tab.key;
              return (
                <Button
                  key={tab.key}
                  type={isActive ? "primary" : "default"}
                  onClick={() => handleTabClick(tab.key)}
                  title={tab.subLabel}
                  style={{
                    marginRight: "10px",
                    backgroundColor: isActive ? tab.activeColor : undefined,
                    borderColor: isActive ? tab.activeColor : undefined,
                    color: isActive ? "#fff" : undefined,
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </div>

          {/* Active tab sub-label */}
          <div style={{ textAlign: "center", marginBottom: "12px", color: "#888", fontSize: "13px" }}>
            {tabs.find((t) => t.key === activeStatusTab)?.subLabel}
          </div>

          {/* Filter Section */}
          <div
            style={{
              background: "#ffffff",
              padding: "15px",
              borderRadius: "10px",
              marginBottom: "15px",
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Employee Name"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Employee Code"
                  value={filters.emp_code}
                  onChange={(e) =>
                    setFilters({ ...filters, emp_code: e.target.value })
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Leave Type"
                  value={filters.leave_type || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, leave_type: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {[
                    ...new Set(
                      leaves.map((l) => l.leave_type).filter(Boolean)
                    ),
                  ].map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  format="DD/MM/YYYY"
                  value={filters.dateRange}
                  onChange={(dates) =>
                    setFilters({ ...filters, dateRange: dates })
                  }
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Organisation"
                  value={filters.organisation || undefined}
                  onChange={(value) =>
                    setFilters({
                      ...filters,
                      organisation: value,
                      department: "",
                      designation: "",
                    })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {Organisations.map((org) => {
                    const key = org.id ?? org.name;
                    const label = org.name ?? String(key);
                    return (
                      <Option key={key} value={key}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Department"
                  value={filters.department || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, department: value, designation: "" })
                  }
                  allowClear
                  style={{ width: "100%" }}
                  disabled={!filters.organisation}
                >
                  {departments.map((dept) => {
                    const key = dept.id ?? dept.name;
                    const label = dept.name ?? String(key);
                    return (
                      <Option key={key} value={key}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Designation"
                  value={filters.designation || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, designation: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                  disabled={!filters.department}
                >
                  {designations.map((des) => {
                    const key = des.id ?? des.name;
                    const label = des.name ?? String(key);
                    return (
                      <Option key={key} value={key}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  type="primary"
                  style={{
                    background: "#0e497b",
                    borderColor: "#0e497b",
                    width: "100%",
                  }}
                  onClick={downloadExcel}
                >
                  Download Excel
                </Button>
              </Col>
            </Row>
          </div>

          {/* Bulk Action Buttons */}
          {selectedRowKeys.length > 0 && (
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <Space>
                <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                  onClick={() => setShowBulkApprove(true)}
                >
                  Bulk Approve
                </Button>
                <Button
                  danger
                  type="primary"
                  icon={<CloseOutlined />}
                  onClick={() => setShowBulkReject(true)}
                >
                  Bulk Reject
                </Button>
                <Button onClick={() => setSelectedRowKeys([])}>
                  Clear Selection
                </Button>
              </Space>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={columns}
                dataSource={filteredLeaves}
                rowKey={(record) => record.id}
                rowSelection={rowSelection}
                bordered
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  pageSize: 25,
                  showSizeChanger: false,
                }}
                onChange={handleTableChange}
                scroll={{ x: "max-content" }}
                components={{
                  header: {
                    cell: (props) => (
                      <th
                        {...props}
                        style={{
                          backgroundColor: "#0e497b",
                          color: "white",
                          marginTop: "20px",
                        }}
                      >
                        {props.children}
                      </th>
                    ),
                  },
                }}
              />
            </div>
          )}

          {/* ── Approve Modal ── */}
          <Modal
            title="Approval Remarks"
            open={showApproveModal}
            footer={null}
            onCancel={() => {
              setShowApproveModal(false);
              setApproveLeave(null);
            }}
            width={600}
            centered
          >
            <p>
              <strong>Employee:</strong> {approveLeave?.employee?.first_name}{" "}
              {approveLeave?.employee?.last_name} (
              {approveLeave?.employee?.emp_code})
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter remarks here (optional)..."
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveLeave(null);
                }}
                disabled={approving}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={approving}
                disabled={approving}
                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                onClick={handleApprove}
              >
                {approving ? "Approving..." : "Approve"}
              </Button>
            </div>
          </Modal>

          {/* ── Reject Modal ── */}
          <Modal
            title="Reason for Rejection"
            open={showRejectModal}
            footer={null}
            onCancel={() => {
              setShowRejectModal(false);
              setRejectLeave(null);
            }}
            width={600}
            centered
          >
            <p>
              <strong>Employee:</strong> {rejectLeave?.employee?.first_name}{" "}
              {rejectLeave?.employee?.last_name} (
              {rejectLeave?.employee?.emp_code})
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter reason here..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              style={{
                borderRadius: "8px",
                marginBottom: "16px",
                borderColor: rejectReason.trim() ? "#d9d9d9" : "red",
              }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectLeave(null);
                }}
                disabled={rejecting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={rejecting}
                disabled={rejecting}
                onClick={handleReject}
              >
                {rejecting ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </Modal>

          {/* ── Bulk Approve Modal ── */}
          <Modal
            title={`Bulk Approve (${selectedRowKeys.length} leaves)`}
            open={showBulkApprove}
            footer={null}
            onCancel={() => {
              setShowBulkApprove(false);
              setBulkRemarks("");
            }}
            width={600}
            centered
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter common remarks for all selected leaves (optional)..."
              value={bulkRemarks}
              onChange={(e) => setBulkRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowBulkApprove(false);
                  setBulkRemarks("");
                }}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={bulkProcessing}
                disabled={bulkProcessing}
                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                onClick={handleBulkApprove}
              >
                {bulkProcessing ? "Approving..." : "Approve All"}
              </Button>
            </div>
          </Modal>

          {/* ── Bulk Reject Modal ── */}
          <Modal
            title={`Bulk Reject (${selectedRowKeys.length} leaves)`}
            open={showBulkReject}
            footer={null}
            onCancel={() => {
              setShowBulkReject(false);
              setBulkRemarks("");
            }}
            width={600}
            centered
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter common rejection reason for all selected leaves..."
              value={bulkRemarks}
              onChange={(e) => setBulkRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowBulkReject(false);
                  setBulkRemarks("");
                }}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={bulkProcessing}
                disabled={bulkProcessing || !bulkRemarks.trim()}
                onClick={handleBulkReject}
              >
                {bulkProcessing ? "Rejecting..." : "Reject All"}
              </Button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}